<template>
    <section id="locator-main-app" class="sl">
        <div class="sl-controls">
            <search-bar @search="handleSearchBtnClick" />
            <book-online-message v-if="state.canBookOnline" />
        </div>

        <div v-if="state.loadMap" class="sl-app">
            <loading-message v-if="state.loading" />
            <google-map v-if="state.loadMap" ref="map" />
            <list v-if="state.loadMap" stores="state.stores" />
        </div>
    </section>
</template>

<script>
import _ from 'underscore';
import axios from 'axios';
import SearchBar from './SearchBar';
import List from './List';
import GoogleMap from './Map';
import LoadingMessage from './LoadingMessage';
import BookOnlineMessage from './BookOnlineMessage';
import GoogleMaps from '../utils/googleMaps';
import LOGIC from '../utils/logic';

const urlParams = new URLSearchParams(window.location.search);

export default {
    components: {
        List,
        GoogleMap,
        SearchBar,
        BookOnlineMessage,
        LoadingMessage
    },

    computed: {
        eventSymbol () {
            return window.eventSymbolPath;
        },

        shouldDisableSearchButton () {
            return ((this.state.searchType || this.state.searchType === '') || !this.state.searchPostalCode || !this.state.searchDistance);
        },

        allowedToSearch () {
            if (this.state.loading || (!this.state.searchType || this.state.searchType === '') || !this.state.searchPostalCode || !this.state.searchDistance) {
                return false;
            }

            return true;
        },
    },

    mounted () {
        for (const prop in window.LocatorConfig) {
            this.state[prop] = window.LocatorConfig[prop];
        }

        if (this.hasSearchTypeParam) {
            let searchType = urlParams.get('type');

            if (searchType === 'retailers') searchType = 'retailer';

            this.state.searchType = searchType;
        }
    },

    methods: {
        handleSearchBtnClick () {
            if (!this.allowedToSearch) return;

            this.state.loading = true;

            this.state.searchPostalCode = (`${this.state.searchPostalCode}`).toUpperCase();

            this.clearStoreList();

            this.state.loadMap = true;

            this.$nextTick(() => {
                this.$refs.map.loadMap();
                this.getGeoCodeDataFromPostalCode();
            });
        },

        clearStoreList () {
            for (let i = this.state.stores.length; i >= 0; i--) {
                this.state.stores.splice(i, 1);
            }
        },

        getGeoCodeDataFromPostalCode () {
            const postalCode = (`${this.state.searchPostalCode}`).trim();

            if (!postalCode || !postalCode.length) return;

            if (this.state.geoCache[postalCode]) {
                this.processGeoCodeData(this.state.geoCache[postalCode], 'OK');
            } else {
                GoogleMaps.geoCodeAddress(postalCode, this.state.countryCode, this.processGeoCodeData);
            }
        },

        processGeoCodeData (results, status) {
            if (status !== 'OK' || !status) {
                return;
            }

            const postalCode = (`${this.state.searchPostalCode}`).trim();

            this.state.geoCache[postalCode] = results;

            if (results.length) {
                GoogleMaps.zoomToGeoCode(results);
                this.getStoreDataByLongLat(results[0].geometry.location.lat(), results[0].geometry.location.lng());
            }
        },

        getStoreDataByLongLat (lat, lng) {
            this.state.loading = true;
            this.getStoresFromApi(lat, lng);
        },

        getCountryCode () {
            if (this.state.appRegion === 'us') return 'US';

            return this.state.countryCode;
        },

        getStoresFromApi (lat, lng) {
            const obj = {
                brand       : 'taylormade',
                distanceUnit: this.distancePref,
                maxdistance : this.state.searchDistance,
                radius      : this.state.searchDistance,
                countryCode : this.state.countryCode
            };

            //if (this.state.appRegion !== 'us') {
            obj.latitude = lat;
            obj.longitude = lng;
            obj.lat = lat;
            obj.long = lng;
            //} else {
            //    obj.postalCode = this.state.searchPostalCode;
            //    obj.countryCode = this.getCountryCode();
            //}

            const locationOfRenyolds = GoogleMaps.getLocationFromLatLng(33.4388113, -83.1953351);
            const thisLocation = GoogleMaps.getLocationFromLatLng(lat, lng);
            const distanceFromReynolds = GoogleMaps.getDistance(locationOfRenyolds, thisLocation);

            // Use Values from Index File for testing
            if (window.location.hostname.match('localhost')) {
                let data = window.retailers;
               
                // automatically add Renyolds Kingdon, if within defined distance
                if (distanceFromReynolds < 450) {
                    data.stores.unshift(window.LocatorConfig.REYNOLDS);
                }
                
                data = this.appendCustomFittingEventData(data.stores);
                data = LOGIC.setStoreStatus(data, this.state);
                this.filterStoresShowResults(data);
            } else {

                axios.get(this.state.api[this.state.searchType], { params: obj }).then((res) => {
                    let { data } = res;

                    try {
                        // TODO FIX THIS CHECKING OF DATA
                        /* eslint-disable */
                      const checkForData = data.stores.length; 
                      /* eslint-enable */
                    } catch (e) {
                        try {
                            data = JSON.parse(res.data.replace('\r', '').replace('\n', ''));
                        } catch (e) {
                            data = {
                                stores: []
                            };
                        }
                    }

                    // automatically add Renyolds Kingdon, if within defined distance
                    if (distanceFromReynolds < 450) {
                        data.stores.unshift(window.LocatorConfig.REYNOLDS);
                    }

                    data = this.appendCustomFittingEventData(data.stores);
                    data = LOGIC.setStoreStatus(data, this.state);
                    this.filterStoresShowResults(data);
                });
            }
        },

        appendCustomFittingEventData (data) {
            if (window.customFittingEvents && window.customFittingEvents.length) {
                window.customFittingEvents.forEach(event => {
                    const index = _.findIndex(data, d => {
                        return d.ID === event.id;
                    });

                    if (index >= 0) {
                        data[index].customEvents = event;
                    }
                });
            }

            return data;
        },

        filterStoresShowResults (data) {
            let filteredStores;

            if (this.state.searchType == 'retailer') {

                //TODO DONT SHOW RETAILERS IN AU IF ITS A FITTER
                // if (this.state.appRegion == 'au') {
                //     filteredStores = _.filter(data, function (obj) {
                //         return !obj._isFitter;
                //     });
                // } else {
                filteredStores = _.filter(data, function () {
                    return true;
                });
                // }
            }

            if (this.state.searchType == 'fittinglocation') {
                filteredStores = _.filter(data, function (obj) {
                    return obj._isFitter || obj._isOther;
                });
            }

            if (this.state.searchType == 'fittingevent') {
                filteredStores = _.filter(data, function (obj) {
                    return obj._isFittingEvent;
                });
            }

            if (this.state.appRegion == 'us') {
                for (let i = 0; i < filteredStores.length; i++) {
                    const store = filteredStores[i];

                    if (store.isNFC) {
                        filteredStores.unshift(filteredStores.splice(i, 1)[0]);
                    }
                }
            }

            // Load the filtered results into the list
            this.state.stores = filteredStores;

            this.placeMarkersOnMap();

            setTimeout(() => {
                this.state.loading = false;
            }, 500);
        },

        placeMarkersOnMap () {
            GoogleMaps.closeInfoBoxes();
            GoogleMaps.clearMarkers();

            for (let i = 0; i < this.state.stores.length; i++) {
                const store = this.state.stores[i];

                if (store) {
                    const markerHtml = LOGIC.createInfoBoxHtml(store);

                    GoogleMaps.createMarker(store, i, LOGIC.markerUrl(store, true), markerHtml, function () {
                        // bus.$emit('ui:disableLive');
                    });
                }
            }

        },
    },
};
</script>

<style scoped>
.sl {
  font-family: arial, helvetica, sans-serif;
  font-size: 16px;
  color: #333;
  padding: 20px 0;
}

.sl-app {
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
}
@media (min-width: 1024px) {
  .sl-app {
    min-height: 600px; 
    flex-direction: row;
  }
}

.sl-controls {
  padding-top: 20px;
  padding-bottom: 20px;
}
</style>